// FeedbackSection — user-submitted product feedback shown in Settings.
//
// Any authenticated user in the tenant can submit a feedback item; owner +
// admin can mark items complete (the text strikes through to signal "done").
//
// Sort: active items first (newest at top), then completed items at bottom.

import { useState, type KeyboardEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Pencil, Trash2, Check, X, ChevronDown, ChevronRight, MessageCircle } from 'lucide-react';
import {
  getFeedback,
  postFeedback,
  patchFeedback,
  deleteFeedback,
  getFeedbackNotes,
  postFeedbackNote,
  patchFeedbackNote,
  deleteFeedbackNote,
  getMe,
  type FeedbackItem,
  type FeedbackNote,
} from '../api/client.js';
import { Card, CardContent } from './ui/card.js';
import { Input } from './ui/input.js';
import { Button } from './ui/button.js';
import { Skeleton } from './ui/skeleton.js';
import { cn } from '../lib/utils.js';

const RTF = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((then - now) / 1000);
  const absSec = Math.abs(diffSec);
  if (absSec < 60) return RTF.format(diffSec, 'second');
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) return RTF.format(diffMin, 'minute');
  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 24) return RTF.format(diffHr, 'hour');
  const diffDay = Math.round(diffHr / 24);
  if (Math.abs(diffDay) < 30) return RTF.format(diffDay, 'day');
  const diffMo = Math.round(diffDay / 30);
  if (Math.abs(diffMo) < 12) return RTF.format(diffMo, 'month');
  return RTF.format(Math.round(diffMo / 12), 'year');
}

function sortFeedback(items: FeedbackItem[]): FeedbackItem[] {
  return [...items].sort((a, b) => {
    if (a.completed !== b.completed) return a.completed ? 1 : -1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

export function FeedbackSection() {
  const queryClient = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe });
  const canToggle = me?.role === 'owner' || me?.role === 'admin';

  const { data: items, isLoading } = useQuery({
    queryKey: ['feedback'],
    queryFn: getFeedback,
  });

  const [draft, setDraft] = useState('');

  const createMutation = useMutation({
    mutationFn: (body: string) => postFeedback({ body }),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, completed }: { id: string; completed: boolean }) =>
      patchFeedback(id, { completed }),
    // Optimistic update: flip the item locally so the strikethrough animates
    // immediately. Server response replaces on success; rollback on error.
    onMutate: async ({ id, completed }) => {
      await queryClient.cancelQueries({ queryKey: ['feedback'] });
      const previous = queryClient.getQueryData<FeedbackItem[]>(['feedback']);
      queryClient.setQueryData<FeedbackItem[]>(['feedback'], (old) =>
        old?.map((item) => (item.id === id ? { ...item, completed } : item)) ?? [],
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['feedback'], ctx.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFeedback(id),
    // Optimistic remove from the list; rollback on error.
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['feedback'] });
      const previous = queryClient.getQueryData<FeedbackItem[]>(['feedback']);
      queryClient.setQueryData<FeedbackItem[]>(['feedback'], (old) =>
        old?.filter((item) => item.id !== id) ?? [],
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['feedback'], ctx.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });

  // Inline edit state: only one item is editable at a time. editDraft holds
  // the in-progress text; Escape cancels (per global UX rule), Enter saves.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  // Expanded set of feedback items (their conversation threads are open).
  // Local state only — does not persist across reloads.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const editMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      patchFeedback(id, { body }),
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: ['feedback'] });
      const previous = queryClient.getQueryData<FeedbackItem[]>(['feedback']);
      queryClient.setQueryData<FeedbackItem[]>(['feedback'], (old) =>
        old?.map((item) => (item.id === id ? { ...item, body } : item)) ?? [],
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['feedback'], ctx.previous);
    },
    onSuccess: () => {
      setEditingId(null);
      setEditDraft('');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });

  const startEdit = (item: FeedbackItem) => {
    setEditingId(item.id);
    setEditDraft(item.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const saveEdit = () => {
    if (!editingId) return;
    const trimmed = editDraft.trim();
    if (!trimmed || trimmed.length > 2000 || editMutation.isPending) return;
    editMutation.mutate({ id: editingId, body: trimmed });
  };

  const handleEditKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const handleSubmit = () => {
    const text = draft.trim();
    if (!text || text.length > 2000 || createMutation.isPending) return;
    createMutation.mutate(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const sorted = items ? sortFeedback(items) : [];

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-start gap-3">
          <MessageSquare className="h-4 w-4 mt-1 shrink-0 text-taupe" />
          <div className="flex-1">
            <p className="text-xs text-taupe">
              Type product feedback or feature requests. Items get marked done as we ship them.
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="What would make this better?"
            disabled={createMutation.isPending}
            maxLength={2000}
            className="flex-1"
          />
          <Button
            onClick={handleSubmit}
            disabled={!draft.trim() || createMutation.isPending}
          >
            Add
          </Button>
        </div>

        {createMutation.error && (
          <div className="text-xs text-destructive">
            {createMutation.error instanceof Error ? createMutation.error.message : 'Submit failed'}
          </div>
        )}

        <div className="space-y-2">
          {isLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : sorted.length === 0 ? (
            <p className="text-sm text-taupe italic">No feedback yet. Be the first.</p>
          ) : (
            sorted.map((item) => {
              const isEditing = editingId === item.id;
              const isExpanded = expandedIds.has(item.id);
              const threadDomId = `feedback-thread-${item.id}`;
              return (
                <div
                  key={item.id}
                  className="rounded-md border border-border bg-card"
                >
                  <div className="flex items-start gap-2 px-3 py-2">
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(item.id)}
                        className="flex items-center gap-1 shrink-0 mt-0.5 text-taupe hover:text-primary transition-colors"
                        aria-expanded={isExpanded}
                        aria-controls={threadDomId}
                        aria-label={
                          isExpanded
                            ? `Collapse conversation (${item.noteCount} ${item.noteCount === 1 ? 'note' : 'notes'})`
                            : `Expand conversation (${item.noteCount} ${item.noteCount === 1 ? 'note' : 'notes'})`
                        }
                        title={isExpanded ? 'Hide conversation' : 'Show conversation'}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                        )}
                        {item.noteCount > 0 && (
                          <span className="flex items-center gap-0.5 text-[11px] font-medium">
                            <MessageCircle className="h-3 w-3" aria-hidden />
                            {item.noteCount}
                          </span>
                        )}
                      </button>
                    )}
                    <input
                      type="checkbox"
                      checked={item.completed}
                      disabled={!canToggle || toggleMutation.isPending || isEditing}
                      onChange={(e) =>
                        toggleMutation.mutate({ id: item.id, completed: e.target.checked })
                      }
                      className="h-4 w-4 mt-0.5 shrink-0 rounded border-border accent-primary disabled:opacity-50"
                      aria-label={item.completed ? 'Mark as not done' : 'Mark as done'}
                    />
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <Input
                          value={editDraft}
                          onChange={(e) => setEditDraft(e.target.value)}
                          onKeyDown={handleEditKeyDown}
                          autoFocus
                          maxLength={2000}
                          disabled={editMutation.isPending}
                          className="text-sm h-8"
                        />
                      ) : (
                        <span
                          className={cn(
                            'text-sm break-words whitespace-pre-wrap',
                            item.completed && 'line-through text-muted-foreground',
                          )}
                        >
                          {item.body}
                        </span>
                      )}
                      {item.completed && !isEditing && (
                        <span className="sr-only">Marked complete</span>
                      )}
                      <div className="mt-1 text-xs text-taupe">
                        {item.authorDisplayName} · {relativeTime(item.createdAt)}
                        {item.completed && item.completedByDisplayName && (
                          <> · done by {item.completedByDisplayName}</>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {canToggle && isEditing && (
                        <>
                          <button
                            type="button"
                            onClick={saveEdit}
                            disabled={!editDraft.trim() || editMutation.isPending}
                            className="text-taupe hover:text-primary transition-colors disabled:opacity-50"
                            aria-label="Save edit"
                            title="Save (Enter)"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            disabled={editMutation.isPending}
                            className="text-taupe hover:text-destructive transition-colors disabled:opacity-50"
                            aria-label="Cancel edit"
                            title="Cancel (Esc)"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {canToggle && !isEditing && (
                        <>
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            className="text-taupe hover:text-primary transition-colors"
                            aria-label="Edit feedback item"
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteMutation.mutate(item.id)}
                            disabled={deleteMutation.isPending}
                            className="text-taupe hover:text-destructive transition-colors disabled:opacity-50"
                            aria-label="Delete feedback item"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <FeedbackThread
                      itemId={item.id}
                      itemBody={item.body}
                      canEdit={canToggle}
                      threadDomId={threadDomId}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// FeedbackThread — child component for the expanded conversation.
// Mounted only when an item is expanded. Calls useQuery unconditionally
// (legal — hooks are at the top level of THIS component, which is itself
// conditionally rendered by the parent).
// ============================================================
interface ThreadProps {
  itemId: string;
  itemBody: string;
  canEdit: boolean;
  threadDomId: string;
}

function FeedbackThread({ itemId, itemBody, canEdit, threadDomId }: ThreadProps) {
  const queryClient = useQueryClient();
  const { data: notes, isLoading } = useQuery({
    queryKey: ['feedback', itemId, 'notes'],
    queryFn: () => getFeedbackNotes(itemId),
  });

  const [draft, setDraft] = useState('');

  const addMutation = useMutation({
    mutationFn: (body: string) => postFeedbackNote(itemId, { body }),
    onSuccess: () => {
      setDraft('');
    },
    onSettled: () => {
      // Both queries are stale: the thread (new note) + parent list (noteCount).
      void queryClient.invalidateQueries({ queryKey: ['feedback', itemId, 'notes'] });
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });

  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteDraft, setEditNoteDraft] = useState('');

  const editMutation = useMutation({
    mutationFn: ({ noteId, body }: { noteId: string; body: string }) =>
      patchFeedbackNote(itemId, noteId, { body }),
    onMutate: async ({ noteId, body }) => {
      await queryClient.cancelQueries({ queryKey: ['feedback', itemId, 'notes'] });
      const previous = queryClient.getQueryData<FeedbackNote[]>(['feedback', itemId, 'notes']);
      queryClient.setQueryData<FeedbackNote[]>(['feedback', itemId, 'notes'], (old) =>
        old?.map((n) => (n.id === noteId ? { ...n, body } : n)) ?? [],
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['feedback', itemId, 'notes'], ctx.previous);
      }
    },
    onSuccess: () => {
      setEditingNoteId(null);
      setEditNoteDraft('');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['feedback', itemId, 'notes'] });
      // No invalidation of ['feedback'] — noteCount didn't change.
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => deleteFeedbackNote(itemId, noteId),
    onMutate: async (noteId) => {
      await queryClient.cancelQueries({ queryKey: ['feedback', itemId, 'notes'] });
      const previous = queryClient.getQueryData<FeedbackNote[]>(['feedback', itemId, 'notes']);
      queryClient.setQueryData<FeedbackNote[]>(['feedback', itemId, 'notes'], (old) =>
        old?.filter((n) => n.id !== noteId) ?? [],
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['feedback', itemId, 'notes'], ctx.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['feedback', itemId, 'notes'] });
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });

  const handleAdd = () => {
    const text = draft.trim();
    if (!text || text.length > 2000 || addMutation.isPending) return;
    addMutation.mutate(text);
  };

  const handleAddKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const startEditNote = (note: FeedbackNote) => {
    setEditingNoteId(note.id);
    setEditNoteDraft(note.body);
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditNoteDraft('');
  };

  const saveEditNote = () => {
    if (!editingNoteId) return;
    const trimmed = editNoteDraft.trim();
    if (!trimmed || trimmed.length > 2000 || editMutation.isPending) return;
    editMutation.mutate({ noteId: editingNoteId, body: trimmed });
  };

  const handleEditNoteKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEditNote();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditNote();
    }
  };

  return (
    <div
      id={threadDomId}
      role="region"
      aria-label={`Conversation for: ${itemBody}`}
      className="border-t border-border bg-secondary/20 pl-12 pr-3 py-3 space-y-3"
    >
      {isLoading ? (
        <Skeleton className="h-8 w-full" />
      ) : notes && notes.length > 0 ? (
        <div className="space-y-2.5">
          {notes.map((note) => {
            const isEditingThisNote = editingNoteId === note.id;
            return (
              <div key={note.id} className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  {isEditingThisNote ? (
                    <Input
                      value={editNoteDraft}
                      onChange={(e) => setEditNoteDraft(e.target.value)}
                      onKeyDown={handleEditNoteKey}
                      autoFocus
                      maxLength={2000}
                      disabled={editMutation.isPending}
                      className="text-sm h-8"
                    />
                  ) : (
                    <span className="text-sm break-words whitespace-pre-wrap">
                      {note.body}
                    </span>
                  )}
                  <div className="mt-0.5 text-[11px] text-taupe">
                    {note.authorDisplayName} · {relativeTime(note.createdAt)}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    {isEditingThisNote ? (
                      <>
                        <button
                          type="button"
                          onClick={saveEditNote}
                          disabled={!editNoteDraft.trim() || editMutation.isPending}
                          className="text-taupe hover:text-primary transition-colors disabled:opacity-50"
                          aria-label="Save note"
                          title="Save (Enter)"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditNote}
                          disabled={editMutation.isPending}
                          className="text-taupe hover:text-destructive transition-colors disabled:opacity-50"
                          aria-label="Cancel edit"
                          title="Cancel (Esc)"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => startEditNote(note)}
                          className="text-taupe hover:text-primary transition-colors"
                          aria-label="Edit note"
                          title="Edit"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteMutation.mutate(note.id)}
                          disabled={deleteMutation.isPending}
                          className="text-taupe hover:text-destructive transition-colors disabled:opacity-50"
                          aria-label="Delete note"
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-taupe italic">Be the first to add a note.</p>
      )}

      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleAddKey}
          placeholder="Add a note…"
          disabled={addMutation.isPending}
          maxLength={2000}
          className="flex-1 h-8 text-sm"
        />
        <Button
          onClick={handleAdd}
          disabled={!draft.trim() || addMutation.isPending}
          size="sm"
        >
          Add note
        </Button>
      </div>

      {addMutation.error && (
        <div className="text-xs text-destructive">
          {addMutation.error instanceof Error ? addMutation.error.message : 'Failed to add note'}
        </div>
      )}
    </div>
  );
}
